import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { getCurrentLevel, getNextLevel, getProgressToNextLevel } from '../utils/levels';
import { LEVELS } from '../utils/levels';
import {BookOpen, Brain, HelpCircle, Trophy, Settings} from 'lucide-react';
import Logo from './Logo';

export default function Header() {
  const { theme } = useTheme();
  const { font } = useFont();
  const { user, signInWithGoogle, signOut } = useAuth();
  const { hasActiveSubscription } = useSubscription();
  const [showLevelHelp, setShowLevelHelp] = useState(false);
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();

  const handleLogoClick = () => {
    if (!user) {
      navigate('/');
    } else {
      navigate('/dashboard');
    }
  };

  const handleAuth = async () => {
    if (user) {
      signOut();
    } else {
      try {
        await signInWithGoogle();
        navigate('/dashboard');
      } catch (error) {
        console.error('Authentication failed:', error);
      }
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };
  return (
    <header className="bg-white shadow-sm" style={{ fontFamily: font.family }}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <button
          className="flex items-center hover:opacity-90 transition-opacity"
          onClick={handleLogoClick}
        >
          <Logo onClick={handleLogoClick} />
        </button>
        <div className="flex items-center space-x-4">
          {user && (
            <>
            <div className="hidden lg:flex items-center space-x-2 mr-4">
              <div className="flex flex-col items-end">
                <div className="flex items-center">
                  <span className="text-sm font-medium mr-2 whitespace-nowrap">Niveau : {user.level}</span>
                  <img
                    src={getCurrentLevel(user.experience || 0).icon}
                    alt={user.level}
                    className="w-6 h-6 object-contain"
                  />
                  <button
                    onClick={() => setShowLevelHelp(true)}
                    className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </button>
                </div>
                {getNextLevel(user.experience || 0) && (
                  <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-${theme.primary} transition-all duration-300`}
                      style={{ width: `${getProgressToNextLevel(user.experience || 0)}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
            <button
              className="hidden md:flex items-center mr-4 hover:opacity-80 transition-opacity cursor-pointer"
              onClick={() => navigate('/profil')}
            >
              {!imageError ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-8 h-8 rounded-full object-cover border border-gray-200"
                  onError={handleImageError}
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-sm">
                  😊
                </div>
              )}
              <span className="ml-2 text-gray-700">{user.name}</span>
            </button>
            </>
          )}
          {user?.role === 'admin' && (
            <button
              onClick={() => navigate('/admin')}
              className={`flex items-center px-3 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors mr-4`}
            >
              <Settings className="h-4 w-4 mr-2" />
              Admin
            </button>
          )}
          <button
            onClick={handleAuth}
            className={`bg-rose-600 text-white px-4 py-2 rounded-md hover:bg-rose-700 hover:text-black transition-colors`}
          >
            {user ? 'Déconnexion' : 'Connexion'}
          </button>
        </div>
      </nav>

      {/* Level Help Modal */}
      {showLevelHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full p-6">
            <h3 className="text-xl font-bold mb-4">Niveaux</h3>
            <div className="space-y-4">
              <p className="text-gray-600">
                Gagnez de l'expérience en complétant des cours et des QCM pour progresser à travers différents niveaux.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <h4 className="font-medium">Comment gagner des points ?</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <BookOpen className="h-4 w-4 mr-2 text-gray-400" />
                    <span>5 points pour chaque cours terminé</span>
                  </li>
                  <li className="flex items-center">
                    <Brain className="h-4 w-4 mr-2 text-gray-400" />
                    <span>5 points pour un QCM non parfait</span>
                  </li>
                  <li className="flex items-center">
                    <Trophy className="h-4 w-4 mr-2 text-gray-400" />
                    <span>10 points pour un QCM à 100%</span>
                  </li>
                </ul>
              </div>
              <div className="space-y-3">
                {LEVELS.map((level, index) => (
                  <div key={level.name} className="flex items-center">
                    <img
                      src={level.icon}
                      alt={level.name}
                      className="w-6 h-6 object-contain mr-3"
                    />
                    <div>
                      <span className="font-medium">{level.name}</span>
                      <span className="text-sm text-gray-500 ml-2">
                        ({level.minExperience} XP
                        {index < LEVELS.length - 1
                          ? ` - ${LEVELS[index + 1].minExperience - 1} XP`
                          : ' et plus'
                        })
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Continuez à apprendre et à vous évaluer pour atteindre le niveau suivant !
              </p>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowLevelHelp(false)}
                className={`px-4 py-2 bg-${theme.primary} text-white rounded-lg hover:opacity-90 transition-opacity`}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}